import React from 'react';
import { Icon } from '@iconify/react';
import { useSet } from 'ccstate-react';
import FileIcon from './FileIcon';
import { navigateToFolder$, toggleFileSelection$, toggleFileStar$ } from '../commands/fileCommands';
import type { FileItem } from '../../common/schema/schemaType';
interface FileListItemProps {
    file: FileItem;
    isSelected: boolean;
    onDoubleClick?: () => void;
    onRightClick?: (e: React.MouseEvent) => void;
}
export default function FileListItem({ file, isSelected, onDoubleClick, onRightClick }: FileListItemProps) {
    const navigateToFolder = useSet(navigateToFolder$);
    const toggleSelection = useSet(toggleFileSelection$);
    const toggleStar = useSet(toggleFileStar$);
    const formatFileSize = (bytes?: number) => {
        if (!bytes)
            return '';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };
    const handleClick = () => {
        toggleSelection(file.id);
    };
    const handleDoubleClick = () => {
        if (file.type === 'folder') {
            navigateToFolder(file.id);
        }
        onDoubleClick?.();
    };
    const handleStarClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleStar(file.id, !file.isStarred);
    };
    const handleRightClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onRightClick?.(e);
    };
    return (<div className={`flex items-center p-3 gap-3 cursor-pointer border-b border-[#E0E7EF] transition-all ${isSelected ? 'bg-[#E8F2FC]' : 'hover:bg-[#F0F4F8]'}`} onClick={handleClick} onDoubleClick={handleDoubleClick} onContextMenu={handleRightClick} data-testid={`file-list-item-${file.id}`} data-id="ej0x0h">
      {/* Icon */}
      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="devnt3">
        <FileIcon file={file} size="md" data-id="fe345r"/>
      </div>

      {/* File name and info */}
      <div className="flex flex-col gap-0.5 flex-1 min-w-0" data-id="lpt4rt">
        <div className="flex items-center gap-2" data-id="0dpydt">
          <span className="text-sm font-medium text-[#1A2B3D] truncate" title={file.name} data-id="vigf2i">
            {file.name}
          </span>

          {/* Indicators */}
          <div className="flex items-center gap-1 flex-shrink-0" data-id="bcovby">
            {file.isStarred && (<button className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#E8F2FC]" onClick={handleStarClick} data-testid={`star-button-${file.id}`} data-id="de4vbl">
                <Icon icon="lucide:star" className="w-3 h-3 text-[#FB8C00] fill-current" data-id="vjlvxe"/>
              </button>)}
            {file.isShared && (<Icon icon="lucide:users" className="w-3 h-3 text-[#4A90E2]" data-id="lzzp13"/>)}
          </div>
        </div>

        <div className="text-xs text-[#8B99A8]" data-id="np96o9">
          {file.type === 'file' ? formatFileSize(file.size) : 'Folder'} • Modified {formatDate(file.modifiedDate)}
        </div>
      </div>

      {/* Size column */}
      <div className="w-20 text-xs text-[#8B99A8] text-right flex-shrink-0" data-id="ehloc2">
        {file.type === 'file' ? formatFileSize(file.size) : '—'}
      </div>

      {/* Date column */}
      <div className="w-24 text-xs text-[#8B99A8] text-right flex-shrink-0" data-id="esojqu">
        {formatDate(file.modifiedDate)}
      </div>

      {/* Action button */}
      <div className="flex items-center justify-center w-6 h-6 rounded hover:bg-[#E8F2FC] flex-shrink-0" data-id="p8386s">
        <Icon icon="lucide:more-horizontal" className="w-4 h-4 text-[#5A6C7D]" data-id="i7dd3f"/>
      </div>
    </div>);
}
